import { FormItem, Heading, TextArea } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form, Modal } from 'antd'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useMedicationSchedulePatternUnpause from '../../hooks/useMedicationSchedulePatternUnpause'
import { getMedicationIcon } from '../../utils/medicationUtils'

interface Medication {
  id: string
  name: string
}

type UnpauseMedicationModalProps = {
  open: boolean
  medication: Medication
  residentName: string
  onCancel: () => void
  onSuccess?: () => void
}

const UnpauseMedicationModal = ({
  open,
  medication,
  residentName,
  onCancel,
  onSuccess
}: UnpauseMedicationModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()
  const reasonUnpaused = Form.useWatch('reasonUnpaused', form)

  const {
    unpauseMedicationSchedulePattern,
    isUnpausingMedicationSchedulePattern
  } = useMedicationSchedulePatternUnpause({
    medicationId: medication.id,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: t('UNPAUSE_MEDICATION_SUCCESS', { medication: medication.name })
      })
      form.resetFields()
      onSuccess?.()
    },
    onError: () => {
      showToast({
        type: 'error',
        title: t('UNPAUSE_MEDICATION_ERROR')
      })
      handleCancel()
    }
  })

  const handleUnpause = async () => {
    try {
      const values = await form.validateFields()
      await unpauseMedicationSchedulePattern(values.reasonUnpaused)
    } catch (error) {
      // Validation failed
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={
        <FooterContainer>
          <ButtonsContainer>
            <Button onClick={handleCancel}>{t('CANCEL')}</Button>
            <Button
              variant="primary"
              onClick={handleUnpause}
              isLoading={isUnpausingMedicationSchedulePattern}
              disabled={!reasonUnpaused?.trim()}
            >
              {t('UNPAUSE_MEDICATION')}
            </Button>
          </ButtonsContainer>
        </FooterContainer>
      }
      width={630}
      title={
        <TitleContainer>
          {getMedicationIcon('active')}
          <ModalTitle>
            <Heading level={3}>{t('UNPAUSE_MEDICATION')}</Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} preserve={false} layout="vertical">
        <ModalContentContainer>
          <Text>
            <Trans
              i18nKey="ARE_YOU_SURE_UNPAUSE_MEDICATION"
              ns={NAMESPACE.EMAR_V2}
              values={{
                medication: medication.name,
                name: residentName
              }}
              components={{ strong: <strong /> }}
            />
          </Text>

          <FormItem
            name="reasonUnpaused"
            label={<Text>{t('REASON_FOR_CHANGE')}</Text>}
            required
            full
            rules={[
              {
                required: true,
                message: t('REASON_FOR_CHANGE_REQUIRED')
              }
            ]}
          >
            <TextArea rows={2} placeholder={t('TYPE_HERE')} />
          </FormItem>
        </ModalContentContainer>
      </Form>

      <Divider />
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 12px;
  margin-bottom: 12px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`

export default UnpauseMedicationModal
